import { motion } from "framer-motion";
import { Calendar, Clock, MapPin } from "lucide-react";

export default function FeaturedEventCard({ event, index, onNavigate }) {
  const formatDate = (dateString) => {
    if (!dateString) return "Date TBA";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return "Date TBA";
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return "";
    try {
      const [hours, minutes] = timeString.split(":");
      const hour12 = parseInt(hours) % 12 || 12;
      const ampm = parseInt(hours) >= 12 ? "PM" : "AM";
      return `${hour12}:${minutes} ${ampm}`;
    } catch {
      return timeString;
    }
  };

  const startDate = event.event_start_date || event.event_date;
  const endDate = event.event_end_date || event.event_date;
  const eventDate =
    startDate && endDate && startDate !== endDate
      ? `${formatDate(startDate)} – ${formatDate(endDate)}`
      : formatDate(startDate || event.event_date);
  const eventTime = formatTime(event.event_time);
  const isConference = event.event_type?.includes("conference");

  return (
    <motion.div
      key={event.slug || index}
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.3 + index * 0.1 }}
      whileHover={{ x: 4, scale: 1.01 }}
      onClick={() => onNavigate(`/event-details/${event.slug}`)}
      className="flex gap-3 p-3 bg-white rounded-lg border border-gray-200 shadow-sm transition-all cursor-pointer hover:border-orange-300 hover:shadow-md group"
    >
      <div className="flex-1 min-w-0">
        <div className="flex gap-2 items-center mb-2">
          <span
            className={`inline-flex px-2 py-0.5 text-xs font-bold rounded-full ${
              isConference
                ? "text-blue-700 bg-blue-50 border border-blue-200"
                : "text-purple-700 bg-purple-50 border border-purple-200"
            }`}
          >
            {isConference ? "Conference" : "Trade Mission"}
          </span>
        </div>

        <h3 className="mb-2 text-sm font-bold text-gray-900 transition-colors line-clamp-2 group-hover:text-orange-600">
          {event.event_title || "Untitled Event"}
        </h3>

        <div className="space-y-1.5">
          {eventDate && (
            <div className="flex gap-1.5 items-center text-xs text-gray-600">
              <Calendar size={12} className="flex-shrink-0 text-emerald-600" />
              <span className="truncate">{eventDate}</span>
              {eventTime && <span className="text-gray-400">•</span>}
              {eventTime && (
                <div className="flex gap-1 items-center">
                  <Clock size={12} className="flex-shrink-0 text-emerald-600" />
                  <span>{eventTime}</span>
                </div>
              )}
            </div>
          )}

          {event.venue_name && (
            <div className="flex gap-1.5 items-center text-xs text-gray-600">
              <MapPin size={12} className="flex-shrink-0 text-blue-600" />
              <span className="truncate">{event.venue_name}</span>
            </div>
          )}
        </div>
      </div>

      <div className="flex-shrink-0">
        <div className="overflow-hidden relative w-20 h-20 bg-gradient-to-br from-emerald-100 via-blue-100 to-purple-100 rounded-lg">
          <img
            src={event.thumbnail}
            alt={event.event_title || "Event"}
            className="object-cover w-full h-full transition-transform group-hover:scale-110"
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t to-transparent from-black/20" />
        </div>
      </div>
    </motion.div>
  );
}

